<template>
  <div
    id="companyIfo"
    ref="companyInfoSection"
    v-if="companyAdminStore.company"
    data-test="company-info-section"
  >
    <div style="display: flex; align-items: center">
      <h2 class="account-card-title">Company Info</h2>
      <div style="width: 25px; height: 18px; padding-left: 8px">
        <Pencil
          :fill="mainColor"
          class="cursor-pointer"
          @click="editInfo"
          v-if="!edit"
          data-test="edit-company-info"
        />
      </div>
    </div>

    <Card
      class="account-card-info card-style"
      v-if="!edit"
      data-test="info-card"
    >
      <template #content>
        <div class="mb-4">
          <div class="field-title">Company Name</div>
          <div
            class="field"
            data-test="company-name"
          >
            {{ companyAdminStore.company.name }}
          </div>
        </div>
        <div class="mb-4">
          <div class="field-title">Company Website</div>
          <div
            class="field"
            data-test="company-website"
          >
            {{ companyAdminStore.company.businessWebsite }}
          </div>
        </div>
        <div class="mb-4">
          <div class="field-title">Employee Giving Verification</div>
          <div class="field">
            <div
              id="aboutUsImage"
              class="right-column about-us-image"
              data-test="employee-giving-verification"
            >
              <div v-if="companyAdminStore.company.verificationType == companyGivingVerificationType.EMPLOYEE_ID">
                Employee ID
              </div>
              <div v-else-if="companyAdminStore.company.verificationType == companyGivingVerificationType.COMPANY_CODE">
                Company Code
              </div>
              <div v-else>Do not require verification</div>
            </div>
          </div>
        </div>
        <div class="mb-4">
          <div class="field-title">Company Logo</div>
          <div class="field">
            <div
              id="aboutUsImage"
              class="right-column about-us-image"
              data-test="logo-image"
            >
              <Image :src="companyAdminStore.company.logo" />
            </div>
          </div>
        </div>
        <div>
          <div class="field-title mb-2">Deactivate Account</div>
          <Button data-test="deactivate-button">Deactivate Account</Button>
        </div>
      </template>
    </Card>

    <Card
      class="account-card-edit-info-grid card-style"
      v-if="edit"
      data-test="edit-card"
    >
      <template #content>
        <Form
          class="flex flex-col gap-6 w-full"
          :resolver="formResolver"
          :initialValues="initialValues"
          ref="companyInfoForm"
          v-slot="$form"
          v-focustrap
        >
          <div class="flex flex-row gap-12">
            <div class="flex-1">
              <LabeledField
                class="mb-6"
                label="Company Name"
                :field="$form.name"
                data-test="edit-company-name"
              >
                <InputText
                  name="name"
                  autoResize
                  fluid
                />
              </LabeledField>
              <LabeledField
                class="mb-6"
                label="Company Website"
                :field="$form.businessWebsite"
                data-test="edit-company-website"
              >
                <InputText
                  name="businessWebsite"
                  type="url"
                  fluid
                />
              </LabeledField>
              <!-- Employee Verification Input  -->
              <LabeledField
                :field="$form.verificationType"
                label="Employee Giving Verification"
              >
                <RadioButtonGroup
                  name="verificationType"
                  class="flex flex-wrap gap-4"
                  data-test="verificationType"
                >
                  <div class="flex items-center gap-2">
                    <RadioButton
                      inputId="employeeId"
                      data-test="employeeId"
                      :value="6435"
                    />
                    <label for="employeeId">Verify with Employee ID</label>
                  </div>
                  <div class="flex items-center gap-2">
                    <RadioButton
                      inputId="verificationCompanyCode"
                      data-test="companyCode"
                      :value="6436"
                    />
                    <label for="verificationCompanyCode">Verify with Company Code</label>
                  </div>
                  <div class="flex items-center gap-2">
                    <RadioButton
                      inputId="noVerification"
                      data-test="noVerification"
                      :value="6437"
                    />
                    <label for="noVerification">Do not require verification</label>
                  </div>
                </RadioButtonGroup>
              </LabeledField>

              <!-- Conditionally Render Company Code Input -->
              <div
                v-show="$form.verificationType?.value === 6436"
                class="flex gap-x-4 w-full"
                data-test="verificationCompanyCodeContainer"
              >
                <LabeledField
                  label="Company Code"
                  :field="$form.verificationCompanyCode"
                  data-test="verificationCompanyCode"
                >
                  <InputText
                    name="verificationCompanyCode"
                    fluid
                  />
                </LabeledField>
              </div>
              <LabeledField
                v-if="isMobile"
                label="Company Logo"
                :field="$form.logo"
                data-test="edit-hero-image-mobile"
              >
                <FormField
                  v-slot="$field"
                  name="logo"
                >
                  <ImageUpload
                    auto
                    :minSize="400"
                    :maxSize="1024"
                    :value="$field"
                    @uploaded="
                      (data) => {
                        $form.logo.value = data;
                      }
                    "
                  />
                  <div class="flex text-sm text-gray-500 my-2">
                    <i class="fas fa-info-circle mr-2 mt-[2px]"></i>
                    <div>Accepted image types: JPG, PNG</div>
                  </div>
                </FormField>
              </LabeledField>
            </div>
            <div
              class="flex-1"
              v-if="!isMobile"
              data-test="edit-hero-image-desktop"
            >
              <LabeledField
                label="Company Logo"
                :field="$form.logo"
              >
                <FormField
                  v-slot="$field"
                  name="logo"
                >
                  <ImageUpload
                    auto
                    :minSize="400"
                    :maxSize="1024"
                    :value="$field"
                    @uploaded="
                      (data) => {
                        $form.logo.value = data;
                      }
                    "
                  />
                  <div class="flex text-sm text-gray-500 my-2">
                    <i class="fas fa-info-circle mr-2 mt-[2px]"></i>
                    <div>Accepted image types: JPG, PNG</div>
                  </div>
                </FormField>
              </LabeledField>
            </div>
          </div>
        </Form>
        <div style="display: flex; justify-content: space-between; grid-column: span 2; height: 50px">
          <Button
            label="Cancel"
            variant="link"
            :style="`color: ${donorStore.mainColor}`"
            @click="edit = false"
            data-test="cancel-button"
          />
          <Button
            label="Save Changes"
            :style="`width: 168px; color: ${getContrastYIQ(donorStore.mainColor)}`"
            :loading="saving"
            @click="updateAccountInfo"
            data-test="save-button"
          />
        </div>
      </template>
    </Card>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted } from 'vue';
import { useCompanyAdminStore } from '@/stores/CompanyAdminStore.js';
import { useDonorStore } from '@/stores/DonorStore.js';
import Pencil from '@/assets/Pencil.vue';
import getContrastYIQ from '@/helpers/getContrastYIQ';
import useIsMobile from '@/composables/useIsMobile.js';
import { z } from 'zod';
import ImageUpload from '@/components/forms/ImageUpload.vue';
import { zodResolver } from '@primevue/forms/resolvers/zod';
import { useToastStore } from '@/stores/ToastStore.js';
const { isMobile } = useIsMobile();
import { companyGivingVerificationType } from '@/helpers/enum.js';

const companyAdminStore = useCompanyAdminStore();
const donorStore = useDonorStore();

const mainColor = donorStore.mainColor;

const edit = ref(false);
const saving = ref(false);

const editInfo = () => {
  edit.value = true;
};

const companyInfoForm = ref(null);

const formResolver = zodResolver(
  z.object({
    name: z.string().max(250, 'Maximum 250 characters allowed'),
    businessWebsite: z.string().url('Invalid URL').nullable(),
    logo: z.string().nullable(),
    verificationType: z.enum(Object.values(companyGivingVerificationType)),
    verificationCompanyCode: z
      .string()
      .optional()
      .refine((val, ctx) => {
        if (ctx.parent.verificationType === companyGivingVerificationType.COMPANY_CODE) {
          return val?.length >= 3;
        }
        return true;
      }, 'Company code must be at least 3 characters when verification type is "Company Code"'),
  }),
);
const initialValues = computed(() => ({
  name: companyAdminStore.company.name || '',
  businessWebsite: companyAdminStore.company.businessWebsite || '',
  logo: companyAdminStore.company.logo || null,
  verificationType: companyAdminStore.company.verificationType || companyGivingVerificationType.EMPLOYEE_ID,
  verificationCompanyCode: companyAdminStore.company.verificationCompanyCode || '',
}));

watch(
  () => companyInfoForm.value?.states?.verificationType?.value,
  (newType) => {
    if (newType !== companyGivingVerificationType.COMPANY_CODE) {
      if (companyInfoForm.value?.states?.verificationCompanyCode) {
        companyInfoForm.value.states.verificationCompanyCode.value = '';
      }
    }
  },
);

const updateAccountInfo = async () => {
  saving.value = true;
  try {
    if (!companyInfoForm.value.valid) {
      useToastStore().add({
        severity: 'error',
        summary: 'Error',
        detail: 'An error occurred while updating your company info.',
        life: 3000,
      });
      return;
    }
    await companyAdminStore.saveForm([companyInfoForm.value, { status: 6434 }]);

    useToastStore().add({
      severity: 'success',
      summary: 'Company Info Updated',
      detail: 'Your company info has been updated successfully.',
      life: 3000,
    });

    edit.value = false;
  } catch (error) {
    useToastStore().add({
      severity: 'error',
      summary: 'Error',
      detail: 'An error occurred while updating your company info.',
      life: 3000,
    });
  }
  saving.value = false;
  // await companyAdminStore.saveForm([{ status: 6434 }]);
};
</script>
<style lang="scss">
.field-title {
  color: #000;
  text-align: center;
  font-family: Poppins;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  text-align: left;
}
.field {
  color: #000;
  text-align: left;
  font-family: Poppins;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
}
.card-info {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}
</style>
